import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Table } from "reactstrap";
import Button from "reactstrap/lib/Button";
import moment from "moment";
import { utils } from "../../../../../utils/utils";
import { DATE_FORMAT, TIME_TYPE_MAPPING } from "../helpers/constants";
import { isNonJobTimeType } from "../helpers/utils";

const WorkOrderRow = ({
  workOrder,
  index,
  workOrderWorkTimes,
  onSeeWODetails,
  onSeeCustomerDetails,
}) => {
  return (
    <tr>
      <td>
        <span
          key={index}
          onClick={() => onSeeWODetails(workOrder.id)}
          className="cursor-pointer text-link"
        >
          {workOrder.jobNo}
        </span>
      </td>
      <td>
        <span
          key={workOrder.customerId}
          onClick={() => onSeeCustomerDetails(workOrder.customerId)}
          className="cursor-pointer text-link"
        >
          {workOrder.customerWorkOrder.customerName}
        </span>
      </td>
      {!workOrder.isNewTimeEntry ? (
        <>
          <td>{workOrderWorkTimes.regular.toFixed(2)}</td>
          <td>{workOrderWorkTimes.overtime.toFixed(2)}</td>
        </>
      ) : (
        <td>
          <FontAwesomeIcon icon={faClock} />
          <span className="ml-1">
            {utils.convertTo12HourFormat(workOrderWorkTimes.startTime)}
          </span>
          <span className="mx-1">/</span>
          <FontAwesomeIcon icon={faClock} color="gray" />
          <span className="ml-1">
            {utils.convertTo12HourFormat(workOrderWorkTimes.endTime)}
          </span>
        </td>
      )}
    </tr>
  );
};

const DateDetailsModal = ({
  entry,
  date,
  rawData,
  otherTimeTypes,
  onSeeWODetails,
  onSeeCustomerDetails,
  onSeeOtherTimeNotes,
}) => {
  const employeeData = rawData.find((d) => d.id === entry.employeeId);
  const dateData = employeeData.payRoll.find((p) =>
    moment(p.date).isSame(date, "date")
  );

  if (employeeData) {
    employeeData.workTimesRaw.forEach((workTime) => {
      if (workTime.employeeWorkDay.isNewTimeEntry) {
        const { hours, overtimeHours } = utils.calculateHours(
          workTime.startTime,
          workTime.endTime,
          dateData.isWeekend,
          dateData.isHoliday
        );
        workTime.hours = hours;
        workTime.overtimeHours = overtimeHours;
      }
    });
  }

  const dateWorkTimes = employeeData.workTimesRaw.filter((p) =>
    moment(p.employeeWorkDay.date).isSame(date, "date")
  );
  const dateOtherTimes = employeeData.otherTimesRaw.filter((p) =>
    moment(p.date).isSame(date, "date")
  );

  const totalHoursDay = (
    dateData.REG +
    dateData.OT +
    dateData.DOUBLE +
    dateOtherTimes.reduce((total, entry) => {
      if (isNonJobTimeType(entry.otherTimeType.name)) {
        return total + entry.hours + entry.overtimeHours;
      }
      return total;
    }, 0)
  ).toFixed(2);

  const totalOtherTimeDay = dateOtherTimes.reduce((total, entry) => {
    if (isNonJobTimeType(entry.otherTimeType.name)) {
      return total + entry.hours + entry.overtimeHours;
    }
    return total;
  }, 0);

  const { newTimeWorkOrders, legacyTimeWorkOrders } =
    dateData?.workOrders.reduce(
      (acc, workOrder) => {
        const existsInNew = acc.newTimeWorkOrders.some(
          (wo) => wo.id === workOrder.id
        );
        const existsInLegacy = acc.legacyTimeWorkOrders.some(
          (wo) => wo.id === workOrder.id
        );

        if (!existsInNew && !existsInLegacy) {
          if (workOrder.isNewTimeEntry) {
            acc.newTimeWorkOrders.push(workOrder);
          } else {
            acc.legacyTimeWorkOrders.push(workOrder);
          }
        }
        return acc;
      },
      { newTimeWorkOrders: [], legacyTimeWorkOrders: [] }
    );

  const otherTimeSummary = otherTimeTypes.reduce((acc, otherTimeType) => {
    const timeData = dateData[otherTimeType];
    if (timeData && (timeData.reg !== 0 || timeData.ot !== 0)) {
      acc[otherTimeType] = timeData;
    }
    return acc;
  }, {});

  return (
    <>
      <h4 className="mb-4">
        {date.format(DATE_FORMAT)} - {totalHoursDay} Total hours
      </h4>
      <Table className="border rounder mb-3" size="small">
        <thead className="small text-muted">
          <tr>
            <th>Regular Time</th>
            <th>Overtime</th>
            {dateData.IS_CALIFORNIA_PAYROLL ? <th>Double Time</th> : null}
            <th>Other</th>
          </tr>
        </thead>
        <tbody>
          {dateData ? (
            <tr>
              <td>{dateData.REG.toFixed(2)}</td>
              <td>{dateData.OT.toFixed(2)}</td>
              {dateData.IS_CALIFORNIA_PAYROLL ? (
                <td>{dateData.DOUBLE.toFixed(2)}</td>
              ) : null}
              <td>{totalOtherTimeDay.toFixed(2)}</td>
            </tr>
          ) : (
            <tr>
              <td colSpan="3" className="text-center">
                No data has been recorded
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div className="mb-2">
        <span className="font-weight-bold">Time Details</span>
      </div>
      {newTimeWorkOrders && newTimeWorkOrders.length ? (
        <Table className="border rounded mb-3" size="small">
          <thead className="small text-muted">
            <tr>
              <th>Work Order</th>
              <th>Customer</th>
              <th>Time Entered</th>
            </tr>
          </thead>
          <tbody>
            {newTimeWorkOrders.map((workOrder, index) => {
              const workOrderWorkTimes = dateWorkTimes.filter(
                (workTime) =>
                  workTime.employeeWorkDay.workOrderEmployee.workOrderTrip
                    .workOrderId === workOrder.id
              );
              return workOrderWorkTimes.map((workTime, wtIndex) => (
                <WorkOrderRow
                  key={`${index}-${wtIndex}`}
                  workOrder={workOrder}
                  index={index}
                  workOrderWorkTimes={workTime}
                  onSeeWODetails={onSeeWODetails}
                  onSeeCustomerDetails={onSeeCustomerDetails}
                />
              ));
            })}
          </tbody>
        </Table>
      ) : null}
      {(legacyTimeWorkOrders && legacyTimeWorkOrders.length) ||
      Object.keys(otherTimeSummary).length ? (
        <Table className="border rounded mb-0" size="small">
          <thead className="small text-muted">
            <tr>
              <th>Work Order</th>
              <th>Customer</th>
              <th>Regular</th>
              <th>Overtime</th>
            </tr>
          </thead>
          <tbody>
            {legacyTimeWorkOrders.map((workOrder, index) => {
              const workOrderWorkTimes = dateWorkTimes
                .filter(
                  (workTime) =>
                    workTime.employeeWorkDay.workOrderEmployee.workOrderTrip
                      .workOrderId === workOrder.id
                )
                .reduce(
                  (total, wt) => {
                    total.regular += wt.hours;
                    total.overtime += wt.overtimeHours;
                    return total;
                  },
                  { regular: 0, overtime: 0 }
                );
              return (
                <WorkOrderRow
                  key={index}
                  workOrder={workOrder}
                  index={index}
                  workOrderWorkTimes={workOrderWorkTimes}
                  onSeeWODetails={onSeeWODetails}
                  onSeeCustomerDetails={onSeeCustomerDetails}
                />
              );
            })}
            {otherTimeTypes.map((otherTimeType, i) => {
              const otherTimeData = dateData[otherTimeType];
              const otherTimeEntries = dateOtherTimes.filter(
                (entry) =>
                  entry.otherTimeType.name ===
                  (TIME_TYPE_MAPPING[otherTimeType] || otherTimeType)
              );
              const hasNotes = otherTimeEntries.some(
                (entry) => entry.note && entry.note.trim() !== ""
              );

              return otherTimeData &&
                (otherTimeData.reg !== 0 || otherTimeData.ot !== 0) ? (
                <tr key={i}>
                  <td>Non Job Time</td>
                  <td>{TIME_TYPE_MAPPING[otherTimeType] || otherTimeType}</td>
                  <td>{otherTimeData.reg.toFixed(2)}</td>
                  <td>{otherTimeData.ot.toFixed(2)}</td>
                  <td>
                    {hasNotes && (
                      <Button
                        color="info"
                        size="sm"
                        title="View notes"
                        onClick={() =>
                          onSeeOtherTimeNotes(otherTimeType, otherTimeEntries)
                        }
                      >
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                        View Notes
                      </Button>
                    )}
                  </td>
                </tr>
              ) : null;
            })}
          </tbody>
        </Table>
      ) : null}
    </>
  );
};

export default DateDetailsModal;
